import { Link, useSearchParams } from 'react-router-dom'
import type { ReactNode } from 'react'

import { LogoMark } from '@/components/layout/Logo'
import { Alert, Card, CardHeader, mutedTextClass } from '@/components/layout/ui'
import { cn } from '@/lib/cn'

/**
 * Landing pages for the stub billing provider.
 *
 * With placeholder Stripe keys the API returns local URLs — `/app/billing/
 * simulated-checkout` and `/app/billing/simulated-portal` — instead of hosted
 * Stripe pages, so the whole upgrade flow is walkable with no credentials and
 * no network. Nothing is activated here: the stub deliberately routes
 * activation through the same webhook pipeline production uses, so the local
 * run exercises the real code path rather than a shortcut.
 */
export function SimulatedCheckoutPage() {
  const [params] = useSearchParams()
  const slug = params.get('org') ?? ''

  return (
    <Frame title="Simulated checkout" backSlug={slug}>
      <Alert tone="warning" title="No payment was taken">
        This deployment has no live Stripe credentials, so the API produced a stand-in checkout
        session instead of a hosted Stripe page.
      </Alert>
      <dl className="mt-5 space-y-2 text-sm">
        <Row label="Session" value={params.get('session_id') ?? '—'} />
        <Row label="Workspace" value={slug || '—'} />
        <Row label="Price" value={params.get('price') ?? '—'} />
        {/* Always 1: every plan is a flat price for the whole workspace. */}
        <Row label="Quantity" value={params.get('quantity') ?? '—'} />
      </dl>
      <p className={cn('mt-5 text-sm leading-relaxed', mutedTextClass)}>
        The subscription is not activated here. In both this deployment and production, a workspace
        moves onto a paid plan only when the billing webhook is processed — so to complete the flow locally,
        deliver a <code className="font-mono text-xs">checkout.session.completed</code> event to{' '}
        <code className="font-mono text-xs">/api/billing/stripe/webhook</code>.
      </p>
    </Frame>
  )
}

export function SimulatedPortalPage() {
  const [params] = useSearchParams()
  const returnUrl = params.get('return_url')
  const slug = returnUrl?.match(/\/app\/([^/]+)\/settings\/billing/)?.[1] ?? ''

  return (
    <Frame title="Simulated billing portal" backSlug={slug}>
      <Alert tone="warning" title="Stand-in for the Stripe portal">
        With live credentials this link opens Stripe's hosted portal, where a customer can update a
        card, download invoices or cancel at period end.
      </Alert>
      <dl className="mt-5 space-y-2 text-sm">
        <Row label="Customer" value={params.get('customer') ?? '—'} />
      </dl>
    </Frame>
  )
}

function Frame({
  title,
  backSlug,
  children,
}: {
  title: string
  backSlug: string
  children: ReactNode
}) {
  return (
    <main className="mx-auto max-w-lg px-6 py-16">
      <Link to="/" className="mb-8 flex items-center gap-2.5" aria-label="Taskly">
        <LogoMark className="size-8" />
        <span className="text-lg font-semibold tracking-tight">Taskly</span>
      </Link>
      <Card>
        <CardHeader title={title} />
        <div className="p-5">
          {children}
          <Link
            to={backSlug ? `/${backSlug}/settings/billing` : '/'}
            className="mt-6 inline-flex h-10 items-center rounded-lg bg-brand-600 px-4 text-sm font-medium text-white transition hover:bg-brand-700"
          >
            Back to billing
          </Link>
        </div>
      </Card>
    </main>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className={mutedTextClass}>{label}</dt>
      <dd className="truncate font-mono text-xs">{value}</dd>
    </div>
  )
}
